/** Client-side PDF generation for HRMS offer letters using jsPDF. */
import { jsPDF } from 'jspdf';

const COMPANY = {
  name: 'Hipro Technologies',
  tagline: 'Software · AI · Growth',
  address: 'India · USA · UK · UAE · Malaysia · Netherlands · Hong Kong',
  email: 'info@hiprotechnologies.com',
};

export const generateOfferPdf = (offer) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const M = 48;

  // Header band
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, W, 88, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(22);
  doc.text(COMPANY.name, M, 44);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  doc.text(COMPANY.tagline, M, 62);
  doc.setFontSize(9); doc.setTextColor(180, 190, 210);
  doc.text(`Offer ID: ${offer.offer_id || ''}`, W - M, 44, { align: 'right' });
  doc.text(new Date().toDateString(), W - M, 60, { align: 'right' });

  // Title
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
  doc.text('Letter of Offer', M, 130);

  // Salutation
  doc.setFont('helvetica', 'normal'); doc.setFontSize(11);
  doc.text(`Dear ${offer.candidate_name || 'Candidate'},`, M, 160);

  const body = [
    `We are delighted to offer you the position of ${offer.designation || ''}${offer.department ? ' in the ' + offer.department + ' department' : ''} at ${COMPANY.name}.`,
    `Your proposed date of joining is ${offer.joining_date || '—'} and you will be based in ${offer.location || '—'}${offer.reporting_manager ? ', reporting to ' + offer.reporting_manager : ''}.`,
    `Your annual compensation (CTC) will be ${offer.ctc || '—'}, inclusive of all statutory components as per the detailed compensation structure to be shared during onboarding.`,
    `The role will have a probation period of ${offer.probation_months || 3} months and a notice period of ${offer.notice_period_days || 60} days on either side after confirmation.`,
    `This offer is contingent on satisfactory background verification, submission of documents and continued good conduct. To accept or decline, please use the secure link shared with this offer.`,
    `We are excited about the value you'll bring to Hipro Technologies and look forward to welcoming you to the team.`,
  ];
  let y = 184;
  body.forEach((para) => {
    const lines = doc.splitTextToSize(para, W - M * 2);
    doc.text(lines, M, y);
    y += lines.length * 14 + 8;
  });

  // Key details box
  y += 6;
  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(M, y, W - M * 2, 110, 8, 8, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text('Key terms', M + 14, y + 22);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  const kv = [
    ['Designation', offer.designation || ''],
    ['Department', offer.department || '—'],
    ['CTC', offer.ctc || '—'],
    ['Joining date', offer.joining_date || '—'],
    ['Location', offer.location || '—'],
    ['Reporting manager', offer.reporting_manager || '—'],
    ['Probation', `${offer.probation_months || 3} months`],
    ['Notice period', `${offer.notice_period_days || 60} days`],
    ['Offer valid until', (offer.expires_at || '').split('T')[0] || '—'],
  ];
  const colW = (W - M * 2 - 28) / 2;
  kv.forEach((pair, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const cx = M + 14 + col * colW;
    const cy = y + 42 + row * 16;
    doc.setTextColor(100, 116, 139); doc.text(pair[0], cx, cy);
    doc.setTextColor(15, 23, 42); doc.text(String(pair[1]), cx + 110, cy);
  });

  // Signature
  y += 130;
  doc.setTextColor(15, 23, 42); doc.setFont('helvetica', 'normal'); doc.setFontSize(11);
  doc.text('Warm regards,', M, y + 16);
  doc.setFont('helvetica', 'bold');
  doc.text('People Team, Hipro Technologies', M, y + 34);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(COMPANY.email + ' · ' + COMPANY.address, M, y + 50);

  // Signature block (audit trail) — printed if present (candidate PDF from public page)
  const sb = offer.signature_block || offer.signature;
  if (sb) {
    y += 70;
    doc.setDrawColor(220, 226, 235);
    doc.setFillColor(240, 253, 244); // emerald-50
    doc.roundedRect(M, y, W - M * 2, 100, 8, 8, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(4, 120, 87);
    doc.text('Digitally signed & audit trail', M + 14, y + 20);
    doc.setFont('courier', 'normal'); doc.setFontSize(8); doc.setTextColor(30, 41, 59);
    doc.text(`Signature: ${(sb.signature || '').slice(0, 64)}`, M + 14, y + 36);
    doc.text(`Document hash: ${(sb.document_hash || '').slice(0, 64)}`, M + 14, y + 50);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(51, 65, 85);
    doc.text(`Signed by: ${sb.signed_by || 'Hipro HRMS'}`, M + 14, y + 64);
    doc.text(`Signed at: ${sb.signed_at || ''}`, M + 14, y + 76);
    (sb.audit_trail || []).slice(0, 3).forEach((ev, i) => {
      doc.text(`\u2022 ${ev.event} at ${ev.at || ''} by ${ev.by || ''}`, M + 14, y + 88 + i * 10);
    });
  }

  doc.save(`Hipro-Offer-${offer.offer_id || offer.candidate_name || 'letter'}.pdf`);
};
